/**
 * Hook for processing route parameters and converting them to filter state
 */

import { useParams, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import { parseRouteFilters, mergeFilters, FilterParams } from '@/utils/filterUtils';
import { SEARCH } from '@/constants/app.constants';

interface RouteParams {
  software?: string;
  holiday?: string;
  genre?: string;
  artist?: string;
}

interface UseRouteFiltersReturn {
  filters: FilterParams;
  routeParams: RouteParams;
  hasRouteFilters: boolean;
  routeType: 'software' | 'holiday' | 'genre' | 'artist' | 'combination' | 'none';
}

/**
 * Hook to process route parameters and search params into unified filter state
 */
export function useRouteFilters(): UseRouteFiltersReturn {
  const params = useParams<RouteParams>();
  const [searchParams] = useSearchParams();

  const result = useMemo(() => {
    // Parse route parameters into filters
    const routeFilters = parseRouteFilters(params);
    
    // Parse query parameters into filters
    const queryFilters: FilterParams = {
      query: searchParams.get('q') || searchParams.get('search') || '',
      software: searchParams.get('software')?.split(',').filter(Boolean) || [],
      holiday: searchParams.get('holiday')?.split(',').filter(Boolean) || [],
      genres: searchParams.get('genres')?.split(',').filter(Boolean) || [],
      artists: searchParams.get('artists')?.split(',').filter(Boolean) || [],
      fileFormats: searchParams.get('fileFormats')?.split(',').filter(Boolean) || [],
      albums: searchParams.get('albums')?.split(',').filter(Boolean) || [],
      minPrice: searchParams.get('minPrice') ? parseInt(searchParams.get('minPrice')!) : undefined,
      maxPrice: searchParams.get('maxPrice') ? parseInt(searchParams.get('maxPrice')!) : undefined,
      featured: searchParams.get('featured') === 'true' ? true : undefined,
      isFree: searchParams.get('free') === 'true' ? true : undefined,
      // Map friendly aliases to valid Typesense sort strings
      sort: (() => {
        const s = (searchParams.get('sort') || '').toLowerCase();
        if (!s) return SEARCH.DEFAULT_SORT;
        // Accept human aliases like "latest", "new", "newest"
        if (s === 'latest' || s === 'new' || s === 'newest' || s === 'recent') {
          return SEARCH.SORT_OPTIONS.NEWEST_FIRST;
        }
        if (s === 'price-asc') return SEARCH.SORT_OPTIONS.PRICE_LOW_TO_HIGH;
        if (s === 'price-desc') return SEARCH.SORT_OPTIONS.PRICE_HIGH_TO_LOW;
        if (s === 'rating' || s === 'top-rated') return SEARCH.SORT_OPTIONS.HIGHEST_RATED;
        // If caller already passed a valid Typesense string, use as-is
        return searchParams.get('sort') || SEARCH.DEFAULT_SORT;
      })(),
      page: searchParams.get('page') ? parseInt(searchParams.get('page')!) : 1,
    };

    // Merge route and query filters (route takes precedence)
    const mergedFilters = mergeFilters(routeFilters, queryFilters);

    // Determine route type
    let routeType: UseRouteFiltersReturn['routeType'] = 'none';
    const hasRouteFilters = Object.keys(routeFilters).some(key => 
      routeFilters[key as keyof FilterParams]
    );

    if (params.software && params.holiday) {
      routeType = 'combination';
    } else if (params.software) {
      routeType = 'software';
    } else if (params.holiday) {
      routeType = 'holiday';
    } else if (params.genre) {
      routeType = 'genre';
    } else if (params.artist) {
      routeType = 'artist';
    }

    return {
      filters: mergedFilters,
      routeParams: params,
      hasRouteFilters,
      routeType
    };
  }, [params, searchParams]);

  return result;
}

/**
 * Hook to determine if current route should show a category landing page
 */
export function useCategoryLandingPage(): {
  shouldShowLanding: boolean;
  categoryType: 'software' | 'holiday' | 'genre' | 'artist' | null;
  categoryValue: string | null;
} {
  const { routeParams, filters } = useRouteFilters();
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    // Show landing page if:
    // 1. We have a single route filter (software OR holiday, not both)
    // 2. No additional query parameters (except basic sorting/pagination)
    // 3. No search query

    const hasSearchQuery = Boolean(filters.query);
    const hasComplexFilters = Boolean(
      (filters.genres && filters.genres.length > 0) ||
      (filters.artists && filters.artists.length > 0) ||
      filters.minPrice !== undefined ||
      filters.maxPrice !== undefined ||
      filters.featured !== undefined ||
      filters.isFree !== undefined
    );

    // Don't show landing if there's a search query or complex filters
    if (hasSearchQuery || hasComplexFilters) {
      return {
        shouldShowLanding: false,
        categoryType: null,
        categoryValue: null
      };
    }

    // Single software filter
    if (routeParams.software && !routeParams.holiday) {
      return {
        shouldShowLanding: true,
        categoryType: 'software',
        categoryValue: routeParams.software
      };
    }

    // Single holiday filter
    if (routeParams.holiday && !routeParams.software) {
      return {
        shouldShowLanding: true,
        categoryType: 'holiday',
        categoryValue: routeParams.holiday
      };
    }

    // Single genre filter
    if (routeParams.genre) {
      return {
        shouldShowLanding: true,
        categoryType: 'genre',
        categoryValue: routeParams.genre
      };
    }

    // Single artist filter
    if (routeParams.artist) {
      return {
        shouldShowLanding: true,
        categoryType: 'artist',
        categoryValue: routeParams.artist
      };
    }

    return {
      shouldShowLanding: false,
      categoryType: null,
      categoryValue: null
    };
  }, [routeParams, filters]);
}